import { useEffect, useState } from 'react'
import { supabase } from '@/lib/supabase'

/**
 * usePetState — focus-you Memory v6+v7 の Pet Companion 状態
 *
 * 関連 spec: .company/departments/ux-design/2026-05-04-pet-companion-design-spec.md (v0.3)
 * 関連 decision: growth_events 64d50a56 (v4 関係性), 5d12c731 (v6 変態), 31416f41 (v5 → superseded)
 *
 * memory_matrix から L0 を pull → species_index 計算 (spec §5) + coverage 計算 + stage 推定
 *
 * MVP 段階:
 *  - species は決定論的 hash (spec §5)
 *  - stage は L0 件数から spec §6 に従って判定
 *  - 表情は emotion_analysis 直近24h 平均から推定 (簡易版)
 *  - 変態は次フェーズ (memory-metamorphosis-check Edge Function 経由)
 */

// 27 種の内部 ID (DB literal). 表示名は @/lib/speciesNames を使う (2026-05-05 archetype 確定).
export type { Species } from '@/lib/speciesNames'
import type { Species } from '@/lib/speciesNames'
import { oenToSpecies } from '@/lib/speciesNames'
export type Stage = 'egg' | 'hatch' | 'juvenile' | 'mature' | 'sage'
export type Emotion = 'calm' | 'joy' | 'sad' | 'tense' | 'tired' | 'curious' | 'gentle' | 'unknown'

export interface PetState {
  species: Species
  speciesIndex: number
  stage: Stage
  l0Count: number
  coverage: number  // 0-100, dense セル数 / 全 36 セル
  emotion: Emotion
  // 変態フェーズ (v6 §15)
  inCocoon: boolean
  cocoonStartedAt: string | null
  // cocoon 開始から 6 日以上経過 = 脱皮期 (最終24h transition)
  hatching: boolean
  // Phase D: OCEAN 推定由来 (任意)
  derivedOEN: string | null
  certaintyAvg: number | null
  // Phase E: 副軸 CA バリエーション (HH/HL/LH/LL) - null は default mature
  derivedCA: string | null
}

const SPECIES: Species[] = ['kitsune', 'lupus', 'avian', 'lutra', 'moss', 'lumen', 'cervus', 'baku']

// シンプルな決定論的文字列 hash (Java String#hashCode 相当)
function hashString(s: string): number {
  let h = 0
  for (let i = 0; i < s.length; i++) {
    h = ((h << 5) - h + s.charCodeAt(i)) | 0
  }
  return Math.abs(h)
}

interface L0Cell {
  domain: string
  category: string | null
  content: string
}

/**
 * spec §5 のマッピング:
 *   species_index = (h(Self) * 7 + h(Work) * 11 + h(Hobby) * 13 + h(Emotion) * 17) mod 8
 * 入力は各ドメインの「支配的価値」(value / core / philosophy など中核 category)
 */
function calcSpeciesIndex(l0: L0Cell[]): number {
  const dominant = (domain: string): string => {
    const hits = l0.filter((c) => c.domain === domain)
    if (hits.length === 0) return ''
    // 中核 category (value/core/philosophy/strength) を優先、なければ最初の content
    const priority = hits.find((c) =>
      c.category && ['value', 'core', 'philosophy', 'strength'].includes(c.category),
    )
    return (priority || hits[0]).content
  }
  const sum =
    hashString(dominant('self')) * 7 +
    hashString(dominant('work')) * 11 +
    hashString(dominant('hobby')) * 13 +
    hashString(dominant('emotion')) * 17
  return sum % 8
}

/**
 * spec §6 ステージ進化:
 *   egg (0-1) → hatch (2-3) → juvenile (4-6) → mature (7-9) → sage (9 + L1 narrative 30+)
 * MVP は L0 件数のみで判定 (sage は L1 narrative テーブル統合後)
 */
function calcStage(l0Count: number): Stage {
  if (l0Count <= 1) return 'egg'
  if (l0Count <= 3) return 'hatch'
  if (l0Count <= 6) return 'juvenile'
  if (l0Count <= 9) return 'mature'
  return 'sage'  // 10+ で sage 扱い (L1 narrative 統合は将来)
}

/**
 * Coverage = dense とマークされた memory_matrix セル数 / 全 36 セル (4 layer × 9 domain)
 * 完全自動化は L1-L3 自動充填パイプライン完成後。MVP は dense 数で粗計算。
 */
function calcCoverage(allRows: L0Cell[] & { coverage?: string }[]): number {
  const denseCount = allRows.filter((r) => (r as { coverage?: string }).coverage === 'dense').length
  return Math.min(100, Math.round((denseCount / 36) * 100))
}

/**
 * 直近24h emotion_analysis から dominantEmotion を取得して spec §7.1 の 8 状態に mapping
 */
function mapEmotion(dominantEmotion: string | null, valence: number | null): Emotion {
  if (!dominantEmotion) return valence != null ? (valence > 0.3 ? 'gentle' : valence < -0.3 ? 'sad' : 'calm') : 'unknown'
  const e = dominantEmotion.toLowerCase()
  if (['joy', 'happy', '喜び'].some((k) => e.includes(k))) return 'joy'
  if (['sad', 'sadness', '悲しみ'].some((k) => e.includes(k))) return 'sad'
  if (['anger', 'tense', '怒り', '緊張'].some((k) => e.includes(k))) return 'tense'
  if (['tired', 'fatigue', '疲れ'].some((k) => e.includes(k))) return 'tired'
  if (['curious', 'interest', '関心'].some((k) => e.includes(k))) return 'curious'
  if (['calm', 'peace', '穏やか'].some((k) => e.includes(k))) return 'calm'
  return 'gentle'
}

export function usePetState() {
  const [state, setState] = useState<PetState | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    let alive = true

    async function fetchState() {
      try {
        // 1. memory_matrix 全件取得 (L0 + coverage 計算用)
        const { data: mmRows, error: mmErr } = await supabase
          .from('memory_matrix')
          .select('layer, domain, category, content, coverage')
          .eq('active', true)
        if (mmErr) throw mmErr

        const allRows = (mmRows || []) as { layer: string; domain: string; category: string | null; content: string; coverage: string }[]
        const l0 = allRows.filter((r) => r.layer === 'L0')

        const speciesIndex = calcSpeciesIndex(l0)
        const stage = calcStage(l0.length)
        const coverage = calcCoverage(allRows)

        // 2. 直近24h の emotion_analysis (簡易)
        let emotion: Emotion = 'calm'
        const since = new Date(Date.now() - 24 * 3600000).toISOString()
        const { data: emoRows } = await supabase
          .from('emotion_analysis')
          .select('dominant_emotion, valence')
          .gte('created_at', since)
          .order('created_at', { ascending: false })
          .limit(1)
        if (emoRows && emoRows.length > 0) {
          const e = emoRows[0] as { dominant_emotion: string | null; valence: number | null }
          emotion = mapEmotion(e.dominant_emotion, e.valence)
        }

        // pet_state から cocoon 状態 + Phase D の派生情報を取得
        const { data: petStateRow } = await supabase
          .from('pet_state')
          .select('in_cocoon, cocoon_started_at, current_species, derived_oen, derived_ca, derived_stage, certainty_avg, smoothed_oen')
          .limit(1).maybeSingle()
        const ps = petStateRow as {
          in_cocoon?: boolean
          cocoon_started_at?: string | null
          current_species?: string
          derived_oen?: string | null
          derived_ca?: string | null
          derived_stage?: Stage | null
          certainty_avg?: number | null
          smoothed_oen?: string | null
        } | null

        // 脱皮期判定: cocoon 開始から 6 日以上 (= 7日中の最後24h)
        const cocoonStartedAt = ps?.cocoon_started_at || null
        let hatching = false
        if (ps?.in_cocoon && cocoonStartedAt) {
          const elapsedHours = (Date.now() - new Date(cocoonStartedAt).getTime()) / 3600000
          hatching = elapsedHours >= 144  // 6日 (144h) 以上経過で脱皮アニメへ
        }

        // Phase D: derived_oen が設定されていれば 27 種マップ → species 解決
        // 解決順位: current_species (確定値) > derived_oen → species (Phase D OCEAN 由来)
        //           > L0 hash 由来の従来 8 種 fallback
        let resolvedSpecies: Species = SPECIES[speciesIndex]
        if (ps?.current_species) {
          resolvedSpecies = ps.current_species as Species
        } else if (ps?.smoothed_oen && oenToSpecies[ps.smoothed_oen]) {
          resolvedSpecies = oenToSpecies[ps.smoothed_oen]
        } else if (ps?.derived_oen && oenToSpecies[ps.derived_oen]) {
          resolvedSpecies = oenToSpecies[ps.derived_oen]
        }

        // stage の解決順位: derived_stage (確信度由来) > L0 件数 fallback
        const resolvedStage: Stage = ps?.derived_stage ?? stage

        if (alive) {
          setState({
            species: resolvedSpecies,
            speciesIndex,
            stage: resolvedStage,
            l0Count: l0.length,
            coverage,
            emotion,
            inCocoon: !!ps?.in_cocoon,
            cocoonStartedAt,
            hatching,
            derivedOEN: ps?.smoothed_oen ?? ps?.derived_oen ?? null,
            certaintyAvg: ps?.certainty_avg ?? null,
            derivedCA: ps?.derived_ca ?? null,
          })
        }
      } catch (e) {
        if (alive) setError((e as Error).message)
      } finally {
        if (alive) setLoading(false)
      }
    }

    fetchState()
    return () => { alive = false }
  }, [])

  return { state, loading, error }
}
